import React from "react";
import PropTypes from "prop-types";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { Grid, InputAdornment } from "@mui/material";
import { format, isValid } from "date-fns";

import { NUMBER_REGEX, blockMinus, limitDigits } from "../../../../../../utils/validation";
import { getInvalidMessage } from "../../../../../../utils/getValidationMessage";
import { DATE_FORMAT } from "../../../../../../constants";
import { DirhamSymbol } from "../../../../../../components/DirhamSymbol";
import {
  Input,
  SelectAutocomplete,
  AutoSaveField as Field,
  DatePicker
} from "../../../../../../components/Form";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { Divider } from "../../../../../../components/Divider";
import { ArrayAddButton } from "../../../Buttons/ArrayAddButton";
import { ButtonWrapper, RemoveButton } from "./styled";
import { documentUnique, downloadableDocsData } from "../../../../../../constants/documents";
import { RoDocument } from "../../../../../UploadDocuments/components/RoDocument/RoDocument";
import { FormContainer } from "../CompanyBusinessRelationships/styled";
import { ScrollToError } from "components/ScrollToError";

const outwardChequeSchema = () =>
  Yup.object().shape({
    cheques: Yup.array().of(
      Yup.object().shape({
        outwardChequeCount6Month: Yup.string()
          .nullable()
          .max(10, "Maximum 10 characters allowed")
          .matches(
            NUMBER_REGEX,
            getInvalidMessage("Total count of outward cheque returns in last 6 months")
          ),
        outwardChequeAmount6Month: Yup.number()
          .nullable()
          .min(0, "Amount must be non-negative")
          .typeError(getInvalidMessage("Amount of outward cheque returns in last 6 months")),
        outwardChequeCount12Month: Yup.string()
          .nullable()
          .max(10, "Maximum 10 characters allowed")
          .matches(
            NUMBER_REGEX,
            getInvalidMessage("Total count of outward cheque returns in last 12 months")
          ),
        outwardChequeAmount12Month: Yup.number()
          .nullable()
          .min(0, "Amount must be non-negative")
          .typeError(getInvalidMessage("Amount of outward cheque returns in last 12 months")),
        percentageOfSales: Yup.number()
          .nullable()
          .min(0, "Percentage must be non-negative")
          .max(100, "Percentage cannot exceed 100")
          .typeError(getInvalidMessage("Percentage of sales")),
        chequeBouncedDate: Yup.date()
          .nullable()
          .max(new Date(), getInvalidMessage("Date of cheque bounced"))
          .typeError(getInvalidMessage("Date of cheque bounced")),
        chequeNumber: Yup.string()
          .nullable()
          .max(10, "Maximum 10 characters allowed")
          .matches(NUMBER_REGEX, getInvalidMessage("Cheque number")),
        bankName: Yup.string()
          .nullable()
          .max(50, "Maximum 50 characters allowed"),
        amount: Yup.number()
          .nullable()
          .min(0, "Amount must be non-negative")
          .typeError(getInvalidMessage("Amount")),
        clearedBy: Yup.string()
          .nullable()
          .max(50, "Maximum 50 characters allowed"),
        clearedDate: Yup.date()
          .nullable()
          .max(new Date(), getInvalidMessage("Cleared on"))
          .typeError(getInvalidMessage("Date of cheque bounced")),
        remarks: Yup.string()
          .nullable()
          .max(120, "Maximum 120 characters allowed")
      })
    )
  });

export const OutwardChequesComponent = ({ handleContinue, updateRoProspect, outwardCheque }) => {
  const changeDateProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(value, DATE_FORMAT) };

  return (
    <Formik
      initialValues={{
        cheques: outwardCheque.map(item => ({
          ...item
        }))
      }}
      onSubmit={handleContinue}
      validationSchema={outwardChequeSchema}
      validateOnChange={false}
    >
      {({ values }) => {
        const outwardCheques = values.cheques.length > 1;
        const amountInputProps = {
          endAdornment: (
            <InputAdornment position="end">
              <DirhamSymbol />
            </InputAdornment>
          ),
          inputProps: { min: 0, step: 0.01, tabIndex: 0, onKeyDown: blockMinus }
        };
        return (
          <FormContainer>
            <ScrollToError />
            <Grid container spacing={3}>
              <FieldArray name="cheques">
                {outwardChequesArrayHelpers => (
                  <>
                    {values.cheques.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <Grid item sm={9} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].totalCount6`}
                            name={`cheques[${index}].outwardChequeCount6Month`}
                            label="Total count of outward cheque returns in last 6 months"
                            placeholder="Total count of outward cheque returns in last 6 months"
                            component={Input}
                          />
                        </Grid>
                        <Grid item sm={9} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].amount6`}
                            name={`cheques[${index}].outwardChequeAmount6Month`}
                            label="Amount of outward cheque returns in last 6 months"
                            placeholder="Amount of outward cheque returns in last 6 months"
                            component={Input}
                            type="number"
                            InputProps={amountInputProps}
                            onInput={limitDigits}
                          />
                        </Grid>
                        <Grid item sm={9} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].totalCount12`}
                            name={`cheques[${index}].outwardChequeCount12Month`}
                            label="Total count of outward cheque returns in last 12 months"
                            placeholder="Total count of outward cheque returns in last 12 months"
                            component={Input}
                          />
                        </Grid>
                        <Grid item sm={9} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].amount12`}
                            name={`cheques[${index}].outwardChequeAmount12Month`}
                            label="Amount of outward cheque returns in last 12 months"
                            placeholder="Amount of outward cheque returns in last 12 months"
                            component={Input}
                            type="number"
                            InputProps={amountInputProps}
                            onInput={limitDigits}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].percentageOfSales`}
                            name={`cheques[${index}].percentageOfSales`}
                            label="Percentage of sales"
                            placeholder="Percentage of sales"
                            component={Input}
                            type="number"
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              inputProps: {
                                min: 0,
                                max: 100,
                                step: 0.01,
                                tabIndex: 0,
                                onKeyDown: blockMinus
                              }
                            }}
                            onInput={limitDigits}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].chequeBouncedDate`}
                            name={`cheques[${index}].chequeBouncedDate`}
                            label="Date of cheque bounced"
                            placeholder="Date of cheque bounced"
                            component={DatePicker}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                            changeProspect={changeDateProspectHandler}
                            maxDate={new Date()}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].chequeNumber`}
                            name={`cheques[${index}].chequeNumber`}
                            label="Cheque number"
                            placeholder="Cheque number"
                            component={Input}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].bankName`}
                            name={`cheques[${index}].bankName`}
                            label="Bank Name"
                            placeholder="Bank Name"
                            component={Input}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].amount`}
                            name={`cheques[${index}].amount`}
                            label="Amount"
                            placeholder="Amount"
                            component={Input}
                            type="number"
                            InputProps={amountInputProps}
                            onInput={limitDigits}
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].clearedBy`}
                            name={`cheques[${index}].clearedBy`}
                            label="Cleared by"
                            component={SelectAutocomplete}
                            datalistId="outwardChequeClearedBy"
                            shrink
                            tabIndex="0"
                          />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].clearedDate`}
                            name={`cheques[${index}].clearedDate`}
                            label="Cleared on"
                            placeholder="Cleared on"
                            component={DatePicker}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                            changeProspect={changeDateProspectHandler}
                            maxDate={new Date()}
                          />
                        </Grid>
                        <Grid item sm={12} xs={12}>
                          <Field
                            // eslint-disable-next-line max-len
                            path={`roProspect.organizationInfoRO.outwardCheque.[${index}].remarks`}
                            name={`cheques[${index}].outwardChequeSpecification`}
                            label="Remarks"
                            placeholder="Remarks"
                            component={Input}
                          />
                        </Grid>
                        {outwardCheques && (
                          <Grid item sm={12} xs={12}>
                            <RemoveButton
                              arrayHelpers={outwardChequesArrayHelpers}
                              itemIndex={index}
                              dataArray={outwardCheque}
                              prospectPath="roProspect.organizationInfoRO.outwardCheque"
                              updateProspect={updateRoProspect}
                              title="Delete"
                            />
                          </Grid>
                        )}
                      </React.Fragment>
                    ))}
                    <Grid item sm={12} xs={12}>
                      <ArrayAddButton
                        title="Add"
                        limit={3}
                        requiredFields={[]}
                        addedItem={{
                          outwardChequeCount6Month: "",
                          outwardChequeAmount6Month: "",
                          outwardChequeCount12Month: "",
                          outwardChequeAmount12Month: "",
                          percentageOfSales: "",
                          chequeBouncedDate: "",
                          chequeNumber: "",
                          bankName: "",
                          amount: "",
                          clearedBy: "",
                          clearedDate: "",
                          remarks: ""
                        }}
                        dataArray={values.cheques}
                        arrayHelpers={outwardChequesArrayHelpers}
                      />
                    </Grid>
                  </>
                )}
              </FieldArray>
              <Grid item sm={12} xs={12}>
                <RoDocument uniqueId={documentUnique.OutwardChequeProof} />
              </Grid>
              <Divider.Light />
              <Grid item sm={12} xs={12}>
                <RoDocument
                  uniqueId={documentUnique.OutwardChequeTemplate}
                  downloadUrl={downloadableDocsData[documentUnique.OutwardChequeTemplate].url}
                  downloadLabel={downloadableDocsData[documentUnique.OutwardChequeTemplate].title}
                />
              </Grid>
            </Grid>

            <ButtonWrapper>
              <ContinueButton type="submit" />
            </ButtonWrapper>
          </FormContainer>
        );
      }}
    </Formik>
  );
};

OutwardChequesComponent.propTypes = {
  /**
   * Function to handle form submission and continue to next step
   */
  handleContinue: PropTypes.func.isRequired,

  /**
   * Function to update RO prospect data in Redux store
   */
  updateRoProspect: PropTypes.func.isRequired,

  /**
   * Array of outward cheque information objects
   */
  outwardCheque: PropTypes.arrayOf(
    PropTypes.shape({
      outwardChequeCount6Month: PropTypes.string,
      outwardChequeAmount6Month: PropTypes.string,
      outwardChequeCount12Month: PropTypes.string,
      outwardChequeAmount12Month: PropTypes.string,
      percentageOfSales: PropTypes.string,
      chequeBouncedDate: PropTypes.string,
      chequeNumber: PropTypes.string,
      bankName: PropTypes.string,
      amount: PropTypes.string,
      clearedBy: PropTypes.string,
      clearedDate: PropTypes.string,
      remarks: PropTypes.string
    })
  ).isRequired
};
